import { Component, OnInit, inject, output } from '@angular/core';
import { Cmg1003SectionTypes, CreditSectionTypes, PropertyTaxCalculationType } from '../../../cmg-1003/models';
import { BehaviorSubject, combineLatest, of, switchMap, take, takeUntil } from 'rxjs';
import { Cmg1003ApplicationInfoStore, Cmg1003EmploymentIncomeExpensesQuery } from '../../../loan-state';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { IdValue } from '../../models';
import { SessionStore } from '../../../core/state';
import { ConfirmationModalCloseType } from '../../../models';
import { CmgUiUtilities } from '../../../cmg-1003/components/common/cmg-ui-utils';
import { Cmg1003Service } from '../../../cmg-1003/services/cmg-1003.service';
import { UnsavedChangesConfirmationComponent } from '../../../cmg-1003/components/unsaved-changes-confirmation/unsaved-changes-confirmation.component';
import { IUnsavedChangesConfirmationViewData } from '../../../cmg-1003/components/unsaved-changes-confirmation/unsaved-changes-view-data';
import { NavigationEnd, Router } from '@angular/router';
import { getPropertyTaxCalculationType } from '../../../cmg-1003/components/employment-income-expenses/expenses/expenses.util';
import { NavbarBaseComponent, NavBarItemBase } from './navbar-base.component';
import { NgClass, AsyncPipe } from '@angular/common';
import { CmgFormControlDomDirective } from '../../directives/cmg-form-control-dom.directive';
import { Select } from 'primeng/select';
import { PrimeTemplate } from 'primeng/api';
import { CmgSplitButtonComponent } from '../../../../../../../libs/ui/src/lib/components/cmg-split-button/cmg-split-button.component';
import { Button } from 'primeng/button';
import { QaSlugPipe } from '../../pipes/qa-slug.pipe';

export type NavBarItem = NavBarItemBase;

@Component({
  selector: 'cmg-clear2-ui-urla-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  imports: [
    NgClass,
    ReactiveFormsModule,
    CmgFormControlDomDirective,
    Select,
    PrimeTemplate,
    CmgSplitButtonComponent,
    FormsModule,
    Button,
    AsyncPipe,
    QaSlugPipe,
  ],
})
export class NavbarComponent extends NavbarBaseComponent implements OnInit {
  private readonly _applicationInfoStore = inject(Cmg1003ApplicationInfoStore);
  private readonly _cmgService = inject(Cmg1003Service);
  private readonly _empIncExpQuery = inject(Cmg1003EmploymentIncomeExpensesQuery);
  private readonly _router = inject(Router);
  private readonly _sessionStore = inject(SessionStore);

  readonly navbarItemClicked = output<CreditSectionTypes>();

  Cmg1003SectionTypes = Cmg1003SectionTypes;
  activeNavbarItem = CreditSectionTypes.requestCreditReport;
  isLoanLockedForManageBorrowers$ = this._cmgStoreQuery.statusRequiresDisabling$();
  displayManageBorrowersDropdown$ = new BehaviorSubject(false);
  routesCanManageBorrowers = ['loansummary'];

  applications$ = combineLatest([this._applicationsQuery.getAllApplications(), this._cmgStoreQuery.statusRequiresDisabling$()]).pipe(
    switchMap(([allApps, disable]) => {
      if (!disable && allApps && !this.hideNewApplication() && !allApps.some((t) => t.id === -1)) {
        allApps.push(new IdValue(-1, 'New Application'));
      }
      /**
       * The PrimeNG Dropdown control is having difficulty setting the values when the options are
       * removed / swapped out dynamically by the New Application workflow so making sure it's set
       * after options are updated
       */
      if (allApps.findIndex((a) => a.id === this.appForm.get('appSelectorDisplay').value) > -1) {
        this.applicationsOptions = allApps;
        setTimeout(() => {
          if (this.appForm.get('appSelector').value !== this.appForm.get('appSelectorDisplay').value) {
            this.appForm.get('appSelectorDisplay').setValue(this.appForm.get('appSelector').value);
          }
        }, 0);
      }
      return of(allApps);
    }),
  );

  applicationsOptions: IdValue<number, string, string>[] = [];

  manageApplicationItems = [
    {
      label: 'Manage Borrowers',
      command: () => {
        this._manageBorrowers();
      },
    },
  ];

  private _manageBorrowers() {
    combineLatest({
      isStateDirty: this._cmgStoreQuery.isStateDirty$(),
    })
      .pipe(take(1))
      .subscribe((currentState) => {
        if (currentState.isStateDirty) {
          const viewData: IUnsavedChangesConfirmationViewData = {
            message: 'The loan must be saved before Manage Borrowers can be opened.',
            headerText: 'Unsaved Changes',
            cancelButtonText: 'No, Cancel',
            saveButtonText: 'Save and Continue',
          };
          const dialogRef = this._dialogService.open(UnsavedChangesConfirmationComponent, {
            data: {
              viewData: viewData,
            },
          });
          dialogRef.onClose.subscribe((save: boolean) => {
            if (save) {
              this._cmgService.save1003(() => {
                this._showManageBorrowersModel();
              }, 'Saving loan...');
            }
          });
        } else {
          this._showManageBorrowersModel();
        }
      });
  }

  ngOnInit() {
    super.ngOnInit();

    this._router.events.pipe(takeUntil(this._destroyed$)).subscribe((events) => {
      if (events instanceof NavigationEnd) {
        this._updateDisplayManageBorrowersDropdown();
      }
    });
    this._updateDisplayManageBorrowersDropdown();
  }

  /**
   * applicationId Positive: On Changing Selection check if application changed, if not then const them switch
   * applicationId Negative: On Changing Selection check if applicationId changed, if not then const them switch
   * @param changeObj
   */
  _onSelectedApplicationChanged = (changeObj: { oldApplicationId: number; newApplicationId: number }) => {
    // check if state is dirty
    if (this._cmgStoreQuery.isStateDirty()) {
      const dialogRef = CmgUiUtilities.showUnsavedChangesModal(
        this._dialogService,
        '<div class="d-flex flex-wrap flex-column p-3"><span>Are you sure you want to leave this page without saving?</span><span>All changes will be lost if they are not saved.</span></div>',
        'Save',
        'Continue without Saving',
        true,
      );
      dialogRef.onClose.subscribe((modalResult: ConfirmationModalCloseType) => {
        if (modalResult === ConfirmationModalCloseType.ClosedViaAcceptButton) {
          const callback =
            changeObj.newApplicationId === -1
              ? this._addNewApplication
              : () => {
                  this._switchApplication(changeObj.newApplicationId);
                };
          this._cmgService.save1003(callback);
        } else if (modalResult === ConfirmationModalCloseType.ClosedViaRejectButton) {
          if (changeObj.oldApplicationId < 0 /* New Application never saved */) {
            this._applicationInfoStore.removeApplication(this._applicationInfoStore.getValue().active as number);
          } else {
            this._cmgStoreQuery.rollback();
            this._cmgStoreQuery.setHead();
          }

          this._goToSelectedApplication(changeObj.newApplicationId);
        } else {
          // User clicked the X
          this._goToSelectedApplication(changeObj.oldApplicationId);
        }
      });
    } else {
      this._goToSelectedApplication(changeObj.newApplicationId);
    }
  };

  private readonly _addNewApplication = () => {
    const propertyTaxCalcType = getPropertyTaxCalculationType(this._empIncExpQuery);
    const newApplicationId = this._applicationInfoStore.addNewApplication(propertyTaxCalcType as PropertyTaxCalculationType);
    this._switchApplication(newApplicationId);
  };

  private readonly _switchApplication = (newApplicationId: number) => {
    this.appForm.get('appSelector').setValue(newApplicationId, { emitEvent: false, onlySelf: true });
    this.appForm.get('appSelectorDisplay').setValue(newApplicationId);
    this._applicationInfoStore.setActive(newApplicationId);
    if (newApplicationId < 0) {
      // TODO: The 'emit' function requires a mandatory void argument
      this.newApplicationAdded.emit();
    } else {
      this._sessionStore.update({ pauseUpdateActiveTab: true });
      // TODO: The 'emit' function requires a mandatory void argument
      this.changeExistingApplication.emit();
    }
  };

  /** Navigates to the selected application, which can be a new or an existing application. */
  private readonly _goToSelectedApplication = (newApplicationId: number) => {
    newApplicationId === -1 ? this._addNewApplication() : this._switchApplication(newApplicationId);
  };

  private _updateDisplayManageBorrowersDropdown() {
    for (const manageBorrowerUrl of this.routesCanManageBorrowers) {
      if (this._router.url?.includes(manageBorrowerUrl)) {
        this.displayManageBorrowersDropdown$.next(true);
        return;
      }
    }
    this.displayManageBorrowersDropdown$.next(false);
  }
}
